import type { ConfigurationContextValue } from "./ConfigurationContext";
// Import the logos as module assets so Vite resolves their URL relative to the
// JS chunk (import.meta.url), not the current route. A plain "./logos/..." path
// breaks on nested routes like /oauth/consent and under a deployment sub-path.
// Opticercle icon: white tile for dark mode and the login pages, black tile for light mode.
import darkModeLogo from "./logos/logo_opticercle_dark.png";
import lightModeLogo from "./logos/logo_opticercle_light.png";

export const defaultDarkModeLogo = darkModeLogo;
export const defaultLightModeLogo = lightModeLogo;

export const defaultCurrency = "CHF";

export const defaultTitle = "OPTICERCLE";

export const defaultCompanySectors = [
  { value: "communication-services", label: "Communication Services" },
  { value: "consumer-discretionary", label: "Consumer Discretionary" },
  { value: "consumer-staples", label: "Consumer Staples" },
  { value: "energy", label: "Energy" },
  { value: "financials", label: "Financials" },
  { value: "health-care", label: "Health Care" },
  { value: "industrials", label: "Industrials" },
  { value: "information-technology", label: "Information Technology" },
  { value: "materials", label: "Materials" },
  { value: "real-estate", label: "Real Estate" },
  { value: "utilities", label: "Utilities" },
];

// Cercle des Bains pipeline. Won and lost are deal statuses, not stages.
export const defaultDealStages = [
  { value: "qualifie", label: "Qualifié" },
  { value: "rdv-pris", label: "RDV pris" },
  { value: "rdv-effectue", label: "RDV effectué" },
  { value: "rdv-non-effectue", label: "RDV non effectué" },
  { value: "evenement-en-cours", label: "Événement en cours d'organisation" },
  { value: "evenement-organise", label: "Événement organisé" },
];

export const defaultDealPipelineStatuses = ["evenement-organise"];

export const defaultNoteStatuses = [
  { value: "cold", label: "Froid", color: "#7dbde8" },
  { value: "warm", label: "Tiède", color: "#e8cb7d" },
  { value: "hot", label: "Chaud", color: "#e88b7d" },
  { value: "in-contract", label: "Sous contrat", color: "#a4e87d" },
];

export const defaultTaskTypes = [
  { value: "none", label: "Aucun" },
  { value: "call", label: "Appel" },
  { value: "email", label: "E-mail" },
  { value: "meeting", label: "Rendez-vous" },
  { value: "follow-up", label: "Relance" },
  { value: "visit", label: "Visite des lieux" },
  { value: "quote", label: "Devis" },
  { value: "lunch", label: "Repas" },
];

// Listes Cercle des Bains, modifiables par le client dans les Réglages.
export const defaultEventTypes = [
  { value: "seminaire", label: "Séminaire" },
  { value: "soiree-entreprise", label: "Soirée d'entreprise" },
  { value: "afterwork", label: "Afterwork" },
  { value: "conference", label: "Conférence" },
  { value: "repas-affaires", label: "Repas d'affaires" },
  { value: "cocktail", label: "Cocktail" },
  { value: "mariage", label: "Mariage" },
  { value: "anniversaire", label: "Anniversaire" },
  { value: "autre", label: "Autre" },
];

export const defaultAcquisitionChannels = [
  { value: "site-web", label: "Site web" },
  { value: "reseaux-sociaux", label: "Réseaux sociaux" },
  { value: "moteur-recherche", label: "Moteur de recherche" },
  { value: "bouche-a-oreille", label: "Bouche-à-oreille" },
  { value: "recommandation", label: "Recommandation" },
  { value: "prospection", label: "Prospection" },
  { value: "salon", label: "Salon / événement" },
  { value: "autre", label: "Autre" },
];

export const defaultProspectStates = [
  { value: "a-contacter", label: "À contacter", color: "#94a3b8" },
  { value: "pas-rep-1", label: "Pas rép. 1", color: "#fcd34d" },
  { value: "pas-rep-2", label: "Pas rép. 2", color: "#f59e0b" },
  { value: "contact-echoue", label: "Contact échoué", color: "#ef4444" },
  { value: "pas-interesse", label: "Pas intéressé", color: "#6b7280" },
  { value: "a-suivre", label: "À suivre", color: "#60a5fa" },
  { value: "qualifie", label: "Qualifié", color: "#818cf8" },
  { value: "rdv-pris", label: "RDV pris", color: "#a78bfa" },
  { value: "rdv-effectue", label: "RDV effectué", color: "#34d399" },
  {
    value: "evenement-organise",
    label: "Événement organisé",
    color: "#10b981",
  },
];

export const defaultCustomerStatuses = [
  { value: "prospect", label: "Prospect", color: "#94a3b8" },
  {
    value: "prospect-grand-compte",
    label: "Prospect grand compte",
    color: "#f59e0b",
  },
  { value: "client", label: "Client", color: "#22c55e" },
  { value: "client-regulier", label: "Client régulier", color: "#15803d" },
];

export const defaultCustomFields = {
  companies: [],
  contacts: [],
  deals: [],
  partners: [],
};

export const defaultStaffTypes = [
  { value: "extra-dj", label: "Extra DJ" },
  { value: "extra-bar", label: "Extra bar" },
  { value: "extra-vestiaire", label: "Extra vestiaire" },
  { value: "extra-secu", label: "Extra sécu" },
  { value: "extra-salle", label: "Extra salle" },
  { value: "extra-cuisine-plonge", label: "Extra cuisine-plonge" },
  { value: "equipe-interne", label: "Équipe interne" },
];

// Placeholders: {{prenom}}, {{nom}}, {{entreprise}}, {{mon_prenom}}, {{mon_nom}}.
// The sender's signature is added after the text.
export const defaultEmailTemplates = [
  {
    name: "Relance",
    subject: "Suite à notre échange",
    body: "Bonjour {{prenom}},\n\nJe me permets de revenir vers vous au sujet de votre projet d'événement au Cercle des Bains.\n\nAvez-vous pu avancer de votre côté ? Je reste à votre disposition pour en parler.\n\nBien cordialement,",
  },
  {
    name: "Envoi de devis",
    subject: "Votre devis - Cercle des Bains",
    body: "Bonjour {{prenom}},\n\nVeuillez trouver ci-joint notre devis pour votre événement.\n\nN'hésitez pas à me contacter pour toute question.\n\nBien cordialement,",
  },
  {
    name: "Confirmation d'événement",
    subject: "Confirmation de votre événement au Cercle des Bains",
    body: "Bonjour {{prenom}},\n\nNous avons le plaisir de vous confirmer votre événement au Cercle des Bains.\n\nNous revenons vers vous très vite pour les derniers détails.\n\nBien cordialement,",
  },
];

export const defaultPartnerTypes = [
  { value: "fournisseur", label: "Fournisseur" },
  { value: "agence-evenementielle", label: "Agence événementielle" },
  { value: "traiteur", label: "Traiteur" },
  { value: "agence-de-secu", label: "Agence de sécu" },
  { value: "photobooth", label: "Photobooth" },
  { value: "son-lumiere", label: "Son-lumière" },
];

// A deal's category is the type of event it is about.
export const defaultDealCategories = defaultEventTypes;

export const defaultConfiguration: ConfigurationContextValue = {
  companySectors: defaultCompanySectors,
  currency: defaultCurrency,
  dealCategories: defaultDealCategories,
  dealPipelineStatuses: defaultDealPipelineStatuses,
  dealStages: defaultDealStages,
  noteStatuses: defaultNoteStatuses,
  taskTypes: defaultTaskTypes,
  title: defaultTitle,
  darkModeLogo: defaultDarkModeLogo,
  lightModeLogo: defaultLightModeLogo,
  eventTypes: defaultEventTypes,
  acquisitionChannels: defaultAcquisitionChannels,
  prospectStates: defaultProspectStates,
  customerStatuses: defaultCustomerStatuses,
  customFields: defaultCustomFields,
  staffTypes: defaultStaffTypes,
  partnerTypes: defaultPartnerTypes,
  emailTemplates: defaultEmailTemplates,
};
